import { useState, useEffect } from 'react';
import { Monitor, Zap, Bell, ClipboardList, AlertTriangle, Check } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';
import { useDashboard } from './dashboardContext.jsx';

const inputS =
  'w-full bg-card border border-rule-2 rounded-lg px-3 py-2 text-fg-2 text-[13px] outline-none box-border';

export function UsersPage() {
  const { user } = useDashboard();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [groupsUser, setGroupsUser] = useState(null);
  const [gmSearch, setGmSearch] = useState('');
  const [form, setForm] = useState({
    email: '',
    displayName: '',
    firstName: '',
    lastName: '',
    password: '',
    roleId: '',
  });
  const [editForm, setEditForm] = useState({});
  const [uPage, setUPage] = useState(0);
  const [search, setSearch] = useState('');
  const U_PAGE_SIZE = 20;
  const { sortRows, headerProps, arrow } = useTableSort('email', 'asc');
  const load = async () => {
    const [u, g, r] = await Promise.all([api.get('/users'), api.get('/platform/groups'), api.get('/roles')]);
    setUsers(u || []);
    setGroups(g || []);
    setRoles(r || []);
    if (!form.roleId && r?.length)
      setForm((f) => ({
        ...f,
        roleId: r.find((x) => x.name === 'Technicien')?.id || r[0].id,
      }));
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);
  const handleCreate = async () => {
    if (!form.email || !form.password) return alert('Email et mot de passe requis');
    await api.post('/users', form);
    setShowCreate(false);
    setForm({
      email: '',
      displayName: '',
      firstName: '',
      lastName: '',
      password: '',
      role: 1,
    });
    load();
  };
  const handleUpdate = async () => {
    if (!editUser) return;
    const body = {};
    if (editForm.email !== editUser.email) body.email = editForm.email;
    if (editForm.displayName !== editUser.displayName) body.displayName = editForm.displayName;
    if (editForm.firstName !== editUser.firstName) body.firstName = editForm.firstName;
    if (editForm.lastName !== editUser.lastName) body.lastName = editForm.lastName;
    if (editForm.phone !== editUser.phone) body.phone = editForm.phone;
    if (editForm.roleId !== editUser.roleId) body.roleId = editForm.roleId;
    if (editForm.isActive !== editUser.isActive) body.isActive = editForm.isActive;
    if (editForm.password) body.password = editForm.password;
    await api.put('/users/' + editUser.id, body);
    setEditUser(null);
    load();
  };
  const handleDelete = async (id) => {
    if (!confirm('Supprimer cet utilisateur?')) return;
    await api.del('/users/' + id);
    load();
  };
  const startEdit = (u) => {
    setEditUser(u);
    setEditForm({
      email: u.email,
      displayName: u.displayName,
      firstName: u.firstName,
      lastName: u.lastName,
      phone: u.phone || '',
      roleId: u.roleId,
      isActive: u.isActive,
      password: '',
    });
  };
  const toggleActive = async (id) => {
    await api.put('/users/' + id + '/toggle-active');
    load();
  };
  // Téléphone perdu : 2FA coupé, sessions révoquées, inscription exigée à la prochaine connexion.
  const reset2fa = async (u) => {
    if (
      !confirm(
        'Réinitialiser le 2FA de ' +
          u.email +
          " ?\nSes sessions seront fermées et il devra enregistrer à nouveau son appli d'authentification à la prochaine connexion."
      )
    )
      return;
    const r = await api.post('/users/' + u.id + '/reset-2fa');
    if (r) {
      alert('2FA réinitialisé pour ' + u.email + '.');
      load();
    } else alert('Réinitialisation refusée.');
  };
  const rolePalette = [
    '#a78bfa',
    '#22d3ee',
    '#94a3b8',
    '#f59e0b',
    '#22c55e',
    '#ef4444',
    '#ec4899',
    '#06b6d4',
  ];
  const rolePaletteBg = [
    'rgba(139,92,246,0.15)',
    'rgba(34,211,238,0.12)',
    'rgba(148,163,184,0.1)',
    'rgba(245,158,11,0.12)',
    'rgba(34,197,94,0.12)',
    'rgba(239,68,68,0.12)',
    'rgba(236,72,153,0.12)',
    'rgba(6,182,212,0.12)',
  ];
  const roleColorFor = (roleId) => {
    const idx = Math.max(
      0,
      roles.findIndex((r) => r.id === roleId)
    );
    return {
      color: rolePalette[idx % rolePalette.length],
      bg: rolePaletteBg[idx % rolePaletteBg.length],
    };
  };
  const inputS =
    'w-full bg-card border border-rule-2 rounded-lg px-3 py-2 text-fg-2 text-[13px] outline-none box-border';
  const getUserGroups = (uid) => groups.filter((g) => g.members.some((m) => m.userId === uid));

  // Groups modal helpers
  const gmUser = groupsUser ? users.find((u) => u.id === groupsUser) : null;
  const gmUserGroups = groupsUser ? getUserGroups(groupsUser) : [];
  const gmAvailable = groups
    .filter((g) => !gmUserGroups.some((ug) => ug.id === g.id))
    .filter((g) => !gmSearch || g.name.toLowerCase().includes(gmSearch.toLowerCase()));
  const addToGroup = async (gid) => {
    await api.post('/platform/groups/' + gid + '/members', {
      userId: groupsUser,
    });
    load();
  };
  const removeFromGroup = async (gid) => {
    await api.del('/platform/groups/' + gid + '/members/' + groupsUser);
    load();
  };
  const filtered = sortRows(
    users.filter(
      (u) =>
        !search ||
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        (u.displayName || '').toLowerCase().includes(search.toLowerCase())
    ),
    (u, key) => {
      if (key === 'roleName') return u.roleName || '';
      if (key === 'groupsCount') return getUserGroups(u.id).length;
      if (key === 'lastLoginAt') return u.lastLoginAt ? new Date(u.lastLoginAt) : null;
      if (key === 'isActive') return u.isActive ? 1 : 0;
      if (key === 'twoFactorEnabled') return u.twoFactorEnabled ? 1 : 0;
      return u[key];
    }
  );
  const totalPages = Math.ceil(filtered.length / U_PAGE_SIZE);
  const paged = filtered.slice(uPage * U_PAGE_SIZE, (uPage + 1) * U_PAGE_SIZE);
  const permIcons = {
    canRemoteConnect: { Icon: Monitor, title: 'Accès distant' },
    canRunCommands: { Icon: Zap, title: 'Exécution de commandes' },
    canManageAlerts: { Icon: Bell, title: 'Gestion des alertes' },
    canViewInventory: { Icon: ClipboardList, title: 'Voir inventaire' },
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h1
          className="text-[22px] font-bold text-fg"
          style={{
            margin: 0,
          }}
        >
          {'Utilisateurs (' + users.length + ')'}
        </h1>
        <div className="flex gap-2.5 items-center">
          <input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setUPage(0);
            }}
            className={`${inputS} w-[200px]`}
          />
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="bg-blue-500 border-none rounded-lg py-2 px-[18px] text-white cursor-pointer text-xs font-semibold whitespace-nowrap shrink-0"
          >
            + Nouvel utilisateur
          </button>
        </div>
      </div>
      {showCreate && (
        <div className="bg-card border border-rule rounded-xl p-5 mb-4">
          <div className="text-sm font-bold text-blue-400 mb-3.5">Nouvel utilisateur</div>
          <div
            className="grid gap-3 mb-3"
            style={{
              gridTemplateColumns: '1fr 1fr',
            }}
          >
            <input
              placeholder="Email"
              value={form.email}
              onChange={(e) =>
                setForm({
                  ...form,
                  email: e.target.value,
                })
              }
              className={inputS}
            />
            <input
              placeholder="Nom affiché"
              value={form.displayName}
              onChange={(e) =>
                setForm({
                  ...form,
                  displayName: e.target.value,
                })
              }
              className={inputS}
            />
            <input
              placeholder="Prénom"
              value={form.firstName}
              onChange={(e) =>
                setForm({
                  ...form,
                  firstName: e.target.value,
                })
              }
              className={inputS}
            />
            <input
              placeholder="Nom"
              value={form.lastName}
              onChange={(e) =>
                setForm({
                  ...form,
                  lastName: e.target.value,
                })
              }
              className={inputS}
            />
            <input
              placeholder="Mot de passe"
              type="password"
              value={form.password}
              onChange={(e) =>
                setForm({
                  ...form,
                  password: e.target.value,
                })
              }
              className={inputS}
            />
            <select
              value={form.roleId}
              onChange={(e) =>
                setForm({
                  ...form,
                  roleId: e.target.value,
                })
              }
              className={inputS}
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              className="bg-ok border-none rounded-lg py-2 px-5 text-white cursor-pointer text-xs font-semibold"
            >
              Créer
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="border border-rule-3 rounded-lg py-2 px-4 text-muted cursor-pointer text-xs"
              style={{
                background: 'none',
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      {editUser && (
        <div
          className="fixed flex items-center justify-center z-[1000]"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
          }}
          onClick={() => setEditUser(null)}
        >
          <div
            className="bg-card border border-rule rounded-2xl p-6"
            style={{
              width: 500,
              maxWidth: '90vw',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-bold text-fg mb-4">Modifier l'utilisateur</div>
            <div
              className="grid gap-3 mb-4"
              style={{
                gridTemplateColumns: '1fr 1fr',
              }}
            >
              <div>
                <label className="text-[11px] text-muted-2 mb-1 block">Email</label>
                <input
                  value={editForm.email || ''}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      email: e.target.value,
                    })
                  }
                  className={inputS}
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-2 mb-1 block">Nom affiché</label>
                <input
                  value={editForm.displayName || ''}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      displayName: e.target.value,
                    })
                  }
                  className={inputS}
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-2 mb-1 block">Prénom</label>
                <input
                  value={editForm.firstName || ''}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      firstName: e.target.value,
                    })
                  }
                  className={inputS}
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-2 mb-1 block">Nom</label>
                <input
                  value={editForm.lastName || ''}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      lastName: e.target.value,
                    })
                  }
                  className={inputS}
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-2 mb-1 block">Téléphone</label>
                <input
                  value={editForm.phone || ''}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      phone: e.target.value,
                    })
                  }
                  className={inputS}
                />
              </div>
              <div>
                <label className="text-[11px] text-muted-2 mb-1 block">Rôle</label>
                <select
                  value={editForm.roleId || ''}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      roleId: e.target.value,
                    })
                  }
                  className={inputS}
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-muted-2 mb-1 block">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={editForm.password || ''}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      password: e.target.value,
                    })
                  }
                  className={inputS}
                  placeholder="Laisser vide pour ne pas changer"
                />
              </div>
              <div
                className="flex items-center gap-2"
                style={{
                  paddingTop: 20,
                }}
              >
                <label className="text-xs text-muted flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        isActive: e.target.checked,
                      })
                    }
                  />
                  Actif
                </label>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setEditUser(null)}
                className="border border-rule-3 rounded-lg py-2 px-4 text-muted cursor-pointer text-xs"
                style={{
                  background: 'none',
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleUpdate}
                className="bg-blue-500 border-none rounded-lg py-2 px-5 text-white cursor-pointer text-xs font-semibold"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
      {
        // Groups management modal
        groupsUser && (
          <div
            className="fixed flex items-center justify-center z-[1000]"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.6)',
            }}
            onClick={() => {
              setGroupsUser(null);
              setGmSearch('');
            }}
          >
            <div
              className="bg-card border border-rule rounded-2xl p-6 flex flex-col"
              style={{
                width: 480,
                maxWidth: '90vw',
                maxHeight: '80vh',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-5">
                <div>
                  <div className="text-base font-bold text-fg">{gmUser?.displayName || gmUser?.email}</div>
                  <div className="text-xs text-muted-2 mt-0.5">Appartenance aux groupes</div>
                </div>
                <button
                  onClick={() => {
                    setGroupsUser(null);
                    setGmSearch('');
                  }}
                  className="border-none text-muted-2 cursor-pointer text-[22px] p-0"
                  style={{
                    background: 'none',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
              <div className="overflow-y-auto flex-1 flex flex-col gap-4">
                {' '}
                <div>
                  <div
                    className="text-[11px] font-semibold text-muted-2 uppercase mb-2"
                    style={{
                      letterSpacing: '0.05em',
                    }}
                  >
                    {'Membre de (' + gmUserGroups.length + ')'}
                  </div>
                  {gmUserGroups.length === 0 ? (
                    <div
                      className="text-[13px] text-muted-3 py-2.5 px-3.5 rounded-lg flex items-center gap-2"
                      style={{
                        background: 'rgba(239,68,68,0.06)',
                        border: '1px solid rgba(239,68,68,0.15)',
                      }}
                    >
                      <AlertTriangle size={14} strokeWidth={1.75} />
                      Aucun groupe — cet utilisateur n'a accès à aucun client.
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      {gmUserGroups.map((g) => {
                        const icons = Object.entries(permIcons).filter(([k]) => g[k]);
                        return (
                          <div
                            key={g.id}
                            className="flex items-center gap-2.5 py-2.5 px-3.5 bg-rule rounded-xl"
                          >
                            <div className="flex-1">
                              <div className="text-[13px] font-semibold text-fg">{g.name}</div>
                              <div className="text-[11px] text-muted-2 mt-0.5">
                                {g.clientAccess.length > 0
                                  ? 'Accès : ' + g.clientAccess.map((ca) => ca.clientName).join(', ')
                                  : 'Aucun client configuré'}
                              </div>
                            </div>
                            {icons.length > 0 && (
                              <div className="flex gap-1.5 text-muted">
                                {icons.map(([k, { Icon, title }]) => (
                                  <Icon key={k} size={14} strokeWidth={1.75} aria-label={title}>
                                    <title>{title}</title>
                                  </Icon>
                                ))}
                              </div>
                            )}
                            <button
                              onClick={() => removeFromGroup(g.id)}
                              title="Retirer du groupe"
                              className="border-none text-muted-2 cursor-pointer text-lg"
                              style={{
                                background: 'none',
                                lineHeight: 1,
                                padding: '0 4px',
                              }}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {
                  // Add to group
                  groups.some((g) => !gmUserGroups.some((ug) => ug.id === g.id)) && (
                    <div>
                      <div
                        className="text-[11px] font-semibold text-muted-2 uppercase mb-2"
                        style={{
                          letterSpacing: '0.05em',
                        }}
                      >
                        Ajouter à un groupe
                      </div>
                      <input
                        placeholder="Rechercher un groupe..."
                        value={gmSearch}
                        onChange={(e) => setGmSearch(e.target.value)}
                        className={`mb-2 ${inputS}`}
                      />
                      {gmAvailable.length === 0 ? (
                        <div className="text-xs text-muted-3">Aucun groupe disponible</div>
                      ) : (
                        <div
                          className="flex flex-col gap-1 overflow-y-auto"
                          style={{
                            maxHeight: 180,
                          }}
                        >
                          {gmAvailable.map((g) => (
                            <div
                              key={g.id}
                              onClick={() => addToGroup(g.id)}
                              className="flex items-center gap-2.5 py-2 px-3.5 bg-app border border-rule-2 rounded-lg cursor-pointer"
                            >
                              <div className="flex-1">
                                <div className="text-[13px] text-fg-2 font-medium">{g.name}</div>
                                {g.description && (
                                  <div className="text-[11px] text-muted-3">{g.description}</div>
                                )}
                              </div>
                              <span
                                className="text-[11px] font-semibold"
                                style={{
                                  color: '#3b82f6',
                                }}
                              >
                                + Ajouter
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                }
              </div>
            </div>
          </div>
        )
      }
      {loading ? (
        <div className="text-muted-2">Chargement...</div>
      ) : (
        <div className="bg-card border border-rule rounded-2xl overflow-hidden">
          <table
            className="w-full text-[12.5px]"
            style={{
              borderCollapse: 'collapse',
            }}
          >
            <thead>
              <tr className="border-b border-rule">
                {[
                  { key: 'email', label: 'Email' },
                  { key: 'displayName', label: 'Nom' },
                  { key: 'roleName', label: 'Rôle' },
                  { key: 'groupsCount', label: 'Groupes' },
                  { key: 'twoFactorEnabled', label: '2FA' },
                  { key: 'lastLoginAt', label: 'Dernière connexion' },
                  { key: 'isActive', label: 'Actif' },
                  { key: null, label: 'Actions' },
                ].map((c) => (
                  <th
                    key={c.label}
                    {...headerProps(c.key)}
                    className="py-2.5 px-3.5 text-left text-muted-2 font-medium text-[11px] uppercase whitespace-nowrap"
                  >
                    {c.label}
                    {arrow(c.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.map((u) => {
                const uGroups = getUserGroups(u.id);
                return (
                  <tr key={u.id} className="border-b border-rule">
                    <td className="py-2.5 px-3.5 text-blue-400 font-mono text-xs">{u.email}</td>
                    <td className="py-2.5 px-3.5 text-fg-2">{u.displayName}</td>
                    <td className="py-2.5 px-3.5">
                      {(() => {
                        const c = roleColorFor(u.roleId);
                        return (
                          <span
                            className="rounded-md text-[11px] font-semibold"
                            style={{
                              background: c.bg,
                              color: c.color,
                              padding: '3px 10px',
                            }}
                          >
                            {u.roleName || '—'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-2.5 px-3.5">
                      {uGroups.length === 0 ? (
                        <span className="text-muted-3 text-[11px]">—</span>
                      ) : (
                        <div className="flex gap-1 flex-wrap items-center">
                          {uGroups.slice(0, 2).map((g) => (
                            <span
                              key={g.id}
                              className="bg-rule text-muted py-0.5 px-2 rounded-md text-[11px] font-medium whitespace-nowrap"
                            >
                              {g.name}
                            </span>
                          ))}
                          {uGroups.length > 2 && (
                            <span className="text-muted-3 text-[11px]">{'+' + (uGroups.length - 2)}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-3.5">
                      {u.twoFactorEnabled ? (
                        <span
                          className="text-[11px]"
                          style={{
                            color: '#22c55e',
                          }}
                        >
                          <Check size={11} strokeWidth={2.5} className="inline mr-0.5" /> Actif
                        </span>
                      ) : (
                        <span className="text-muted-2 text-[11px]">Non</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3.5 text-muted text-[11px]">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('fr-FR') : '—'}
                    </td>
                    <td className="py-2.5 px-3.5">
                      <div
                        className="h-2"
                        style={{
                          width: 8,
                          borderRadius: '50%',
                          background: u.isActive ? '#22c55e' : '#ef4444',
                        }}
                      />
                    </td>
                    <td className="py-2.5 px-3.5 flex gap-1.5 flex-wrap">
                      <button
                        onClick={() => startEdit(u)}
                        className="border border-rule-3 rounded-md py-1 px-2.5 text-blue-400 cursor-pointer text-[11px]"
                        style={{
                          background: 'none',
                        }}
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => {
                          setGroupsUser(u.id);
                          setGmSearch('');
                        }}
                        className="border border-rule-3 rounded-md py-1 px-2.5 text-violet-400 cursor-pointer text-[11px]"
                        style={{
                          background: 'none',
                        }}
                      >
                        {uGroups.length ? 'Groupes (' + uGroups.length + ')' : 'Groupes'}
                      </button>
                      {u.twoFactorEnabled && u.id !== user?.id && (
                        <button
                          onClick={() => reset2fa(u)}
                          title="Téléphone perdu : désactive le 2FA et ferme les sessions"
                          className="border border-rule-3 rounded-md py-1 px-2.5 text-warn cursor-pointer text-[11px]"
                          style={{
                            background: 'none',
                          }}
                        >
                          Réinitialiser 2FA
                        </button>
                      )}
                      <button
                        onClick={() => toggleActive(u.id)}
                        className="border border-rule-3 rounded-md py-1 px-2.5 text-muted cursor-pointer text-[11px]"
                        style={{
                          background: 'none',
                        }}
                      >
                        {u.isActive ? 'Désactiver' : 'Activer'}
                      </button>
                      {!u.isGlobalAdmin && (
                        <button
                          onClick={() => handleDelete(u.id)}
                          className="rounded-md py-1 px-2.5 text-red-300 cursor-pointer text-[11px]"
                          style={{
                            background: 'none',
                            border: '1px solid #7f1d1d',
                          }}
                        >
                          Supprimer
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div
              className="flex justify-center gap-1.5"
              style={{
                padding: '14px 0',
              }}
            >
              <button
                onClick={() => setUPage(Math.max(0, uPage - 1))}
                disabled={uPage === 0}
                className="bg-rule border-none rounded-md py-1.5 px-3.5 text-xs font-semibold"
                style={{
                  color: uPage === 0 ? '#475569' : '#e2e8f0',
                  cursor: uPage === 0 ? 'default' : 'pointer',
                }}
              >
                ←
              </button>
              {Array.from(
                {
                  length: Math.min(totalPages, 7),
                },
                (_, i) => {
                  let p = i;
                  if (totalPages > 7) {
                    if (uPage < 4) p = i;
                    else if (uPage > totalPages - 4) p = totalPages - 7 + i;
                    else p = uPage - 3 + i;
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => setUPage(p)}
                      className="border-none rounded-md py-1.5 px-2.5 cursor-pointer text-xs font-semibold"
                      style={{
                        background: p === uPage ? '#3b82f6' : '#1a1f2e',
                        color: p === uPage ? '#fff' : '#94a3b8',
                        minWidth: 32,
                      }}
                    >
                      {p + 1}
                    </button>
                  );
                }
              )}
              <button
                onClick={() => setUPage(Math.min(totalPages - 1, uPage + 1))}
                disabled={uPage >= totalPages - 1}
                className="bg-rule border-none rounded-md py-1.5 px-3.5 text-xs font-semibold"
                style={{
                  color: uPage >= totalPages - 1 ? '#475569' : '#e2e8f0',
                  cursor: uPage >= totalPages - 1 ? 'default' : 'pointer',
                }}
              >
                →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
